import Loader from '@amzn/meridian/loader';
import React, { FC, RefObject, useCallback, useState } from 'react';
import { helpToDocx } from '../../utils/helpToDocx';
import { Button, useTranslation } from '../blocks';
import { Download } from '../icons';
import { usePageMessaging } from './PageMessaging';

interface DownloadHelpButtonProps {
    contentRef: RefObject<HTMLElement>;
    filename: string;
}

function triggerDownload(blob: Blob, filename: string) {
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    setTimeout(() => window.URL.revokeObjectURL(url), 1000);
}

const DownloadHelpButton: FC<DownloadHelpButtonProps> = ({ contentRef, filename }) => {
    const [isDownloading, setIsDownloading] = useState(false);
    const { t } = useTranslation('introductionHelp');
    const { showSuccess, showError } = usePageMessaging();

    const handleDownload = useCallback(async () => {
        if (!contentRef.current) return;

        // Snapshot the DOM content before any async work to avoid race conditions
        // if the user navigates to a different help topic while downloading
        const snapshot = contentRef.current.cloneNode(true) as HTMLElement;

        setIsDownloading(true);
        try {
            const blob = await helpToDocx(snapshot);
            triggerDownload(blob, filename);
            showSuccess(t('helpPage_downloadSuccess'));
        } catch {
            showError(t('helpPage_downloadError'));
        } finally {
            setIsDownloading(false);
        }
    }, [contentRef, filename, t, showSuccess, showError]);

    return (
        <Button onClick={handleDownload} disabled={isDownloading}>
            {isDownloading ? <Loader size={'small'} /> : <Download />}
            <p>{t('helpPage_downloadWord')}</p>
        </Button>
    );
};

export { DownloadHelpButton };
